import { Component, Input, OnChanges, ChangeDetectionStrategy, TemplateRef } from '@angular/core';
import { Order } from "../../models/order.model";
import { AuthService } from "../../services/auth.service";
import { TranslateService } from "@ngx-translate/core";
import { FormControl, FormGroup } from "@angular/forms";
import { OrderService } from "../../services/order.service";
import { Quotation } from "../../models/quotation.model";
import { QuotationService } from "../../services/quotation.service";
import { ShipmentGroup } from "../../models/shipment-group.interface";
import { ShipmentService } from "../../services/shipment.service";
import { RolesEnum } from "../../enums/roles.enum";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

@Component({
    selector: 'app-lock-by-editor',
    templateUrl: './lock-by-editor.component.html',
    styleUrls: ['./lock-by-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LockByEditorComponent implements OnChanges {
  @Input() object: Order | Quotation | ShipmentGroup;
  @Input() isMenuItem = false;
  public orderForm: FormGroup = null;
  public progress = false;
  private dialogRef: MatDialogRef<any> = null;

  /**
   * Get tooltip text based on lock and comment status
   */
  public getTooltipText(): string {
    if (!this.object) return '';
    const comment = this.object.lockComment ? `\n💬 ${this.object.lockComment}` : '';
    if (this.object.lockerName) {
      return `${this.translate.instant('ADMIN.MANAGER')} : ${this.object.lockerName}${comment}`;
    }
    return this.object.lockComment ? this.object.lockComment : this.translate.instant('ADMIN.NONE');
  }

  constructor(public authService: AuthService,
              public translate: TranslateService,
              private orderService: OrderService,
              private quotationService: QuotationService,
              private shipmentService: ShipmentService,
              private dialog: MatDialog) {
  }

  public ngOnChanges(): void {
    this.initLockByForm();
  }

  /**
   * Open the lock settings dialog
   * @param templateRef The dialog template
   * @param event The click event to prevent propagation/default
   */
  public openLockDialog(templateRef: TemplateRef<any>, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.initLockByForm();
    this.dialogRef = this.dialog.open(templateRef, {
      width: '360px',
      maxWidth: '95vw',
      panelClass: 'premium-lock-dialog',
    });
  }

  /**
   * Close the active dialog if open
   */
  public closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }

  /**
   * Prepare order to be locked
   * @return {void}
   */
  public lock(): void {
    this.orderForm.controls.lockedBy.setValue(this.authService.getUid);
    this.orderForm.controls.lockerName.setValue(this.authService.currentUser.displayName);
    this.orderForm.controls.lockDate.setValue(new Date());
  }

  /**
   * Prepare order to be unlocked
   * @return {void}
   */
  public unlock(): void {
    this.orderForm.controls.lockedBy.setValue('none');
    this.orderForm.controls.lockerName.setValue('');
    this.orderForm.controls.lockDate.setValue(new Date());
  }

  /**
   * Save order in database
   * @param order The updated order
   */
  public updateOrder(order: Order | Quotation): void {
    this.progress = true;
    this.object.lockedBy = order.lockedBy;
    this.object.lockComment = order.lockComment;
    this.object.lockerName = order.lockerName;
    this.object.lockDate = order.lockDate;
    const updatedObject = {
      lockedBy: this.object.lockedBy,
      lockComment: this.object.lockComment,
      lockerName: this.object.lockerName,
      lockDate: this.object.lockDate,
    };
    if (this.object instanceof Quotation) {
      this.quotationService.update(this.object.id, updatedObject).catch(err => {
        console.error(err);
      }).finally(() => {
        this.progress = false;
        this.closeDialog();
      });
      return;
    } else if (this.object instanceof Order) {
      this.orderService.update(this.object.id, updatedObject).catch(err => {
        console.error(err);
      }).finally(() => {
        this.progress = false;
        this.closeDialog();
      });
    } else {
      this.shipmentService.updateComment(this.object, updatedObject).catch(err => {
        console.error(err);
      }).finally(() => {
        this.progress = false;
        this.closeDialog();
      });
    }
  }

  /**
   * Initialize the form to edit the order
   */
  public initLockByForm(): void {
    this.orderForm = new FormGroup({
      lockComment: new FormControl(this.object.lockComment, {nonNullable: true}),
      lockedBy: new FormControl(this.object.lockedBy, {nonNullable: true}),
      lockerName: new FormControl(this.object.lockerName, {nonNullable: true}),
      lockDate: new FormControl(this.object.lockDate, {nonNullable: true}),
    });
  }

  protected readonly RolesEnum = RolesEnum;
}
